import React, { useState, useMemo } from "react";
import { Button, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDispatch, useSelector } from "react-redux";
import { ReduxState } from "../redux";
import { fixBankPageActionAsync } from "../redux/reducers/FixBankPageAsync";

interface FixBankPageWithAIButtonProps {
  selectedDocumentId: string | null;
  jobDocs: any[];
  variant?: string;
  size?: "sm" | "lg";
  className?: string;
}

export const FixBankPageWithAIButton: React.FC<
  FixBankPageWithAIButtonProps
> = ({
  selectedDocumentId,
  jobDocs,
  variant = "outline-primary",
  size,
  className = "",
}) => {
  const dispatch = useDispatch();
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const { loading } = useSelector(
    (state: ReduxState) => state.FixBankPageAsync
  );

  // Find the selected document to get page count
  const selectedDoc = useMemo(() => {
    if (!selectedDocumentId || !jobDocs) return null;
    return jobDocs.find(
      (doc) => doc.documentId.toString() === selectedDocumentId.toString()
    );
  }, [selectedDocumentId, jobDocs]);

  // Generate page options
  const pageOptions = useMemo(() => {
    if (!selectedDoc || !selectedDoc.numPages) return [];

    const pages: Array<{ pageNumber: number; label: string }> = [];
    for (let i = 1; i <= selectedDoc.numPages; i++) {
      pages.push({
        pageNumber: i,
        label: `Page ${i}`,
      });
    }
    return pages;
  }, [selectedDoc]);

  const handleFixPage = () => {
    if (!selectedDocumentId || !selectedDoc) return;

    dispatch(
      fixBankPageActionAsync({
        documentId: parseInt(selectedDocumentId),
        pageNumber: selectedPage - 1, // API expects 0-based page numbers
      })
    );
  };

  // Don't render if no document is selected or document has no pages
  if (!selectedDocumentId || !selectedDoc || pageOptions.length === 0) {
    return null;
  }

  return (
    <div className={`d-flex align-items-center ${className}`}>
      {pageOptions.length > 0 && (
        <Form.Control
          as="select"
          size={size}
          value={selectedPage}
          onChange={(e) => setSelectedPage(parseInt(e.target.value))}
          disabled={loading}
          className="mr-2"
          style={{ width: "auto", maxHeight: "120px", overflowY: "auto" }}
        >
          {pageOptions.map((page) => (
            <option key={page.pageNumber} value={page.pageNumber}>
              {page.label}
            </option>
          ))}
        </Form.Control>
      )}

      <Button
        variant={variant}
        size={size}
        onClick={handleFixPage}
        disabled={loading}
        className="d-flex align-items-center"
      >
        <FontAwesomeIcon
          icon={loading ? "spinner" : "magic"}
          spin={loading}
          className="mr-2"
        />
        {loading ? "Fixing..." : "Fix with AI"}
      </Button>
    </div>
  );
};
